import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap } from '@cm/core/localization';

const pathStart = 'Catalogs|Exam|';
const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	htmlAllowed: false,
	feature: HubFeature.Exam,
	portal: 'SP',
};
const columnHeadingDescriptionStart = 'Column heading for: ';

type LocNameStart = 'SPExamCatalog';
export type SPExamCatalogLocNames =
	| `${LocNameStart}TitlePage`
	| `${LocNameStart}LabelStatus`
	| `${LocNameStart}LabelFilterByStatus`
	| `${LocNameStart}LabelGroup`
	| `${LocNameStart}LabelFilterByGroup`
	| `${LocNameStart}LabelCode`
	| `${LocNameStart}LabelName`
	| `${LocNameStart}LabelNameAbbreviation`
	| `${LocNameStart}LabelStartDate`
	| `${LocNameStart}LabelEndDate`
	| `${LocNameStart}LabelValid`
	| `${LocNameStart}LabelScoreReportEnabled`
	| `${LocNameStart}LabelBadgingEnabled`
	| `${LocNameStart}ButtonCriteria`
	| `${LocNameStart}ContentForever`;

export const spExamCatalogLocs: LocDefs<SPExamCatalogLocNames> = {
	SPExamCatalogTitlePage: {
		defaultValue: 'Exams Catalog',
		description: 'Page Title',
		displayPath: `${pathStart}Page Title`,
		displayType: 'Title',
		legacyName: 'Exam/Catalogs.Exams.Title.PageTitle',
		...sharedProperties,
	},
	SPExamCatalogLabelStatus: {
		defaultValue: 'Status',
		description: `${columnHeadingDescriptionStart}Status`,
		displayPath: `${pathStart}Status`,
		displayType: 'Label',
		legacyName: 'Exam/Catalogs.Exams.ColumnHeading.Status',
		...sharedProperties,
	},
	SPExamCatalogLabelFilterByStatus: {
		defaultValue: 'Filter by Status',
		description: `Label for: Filter by Status`,
		displayPath: `${pathStart}Filter by Status`,
		displayType: 'Label',
		legacyName: 'Exam/Catalogs.Exams.FieldLabel.FilterByStatus',
		...sharedProperties,
	},
	SPExamCatalogLabelGroup: {
		defaultValue: 'Group',
		description: `${columnHeadingDescriptionStart}Group`,
		displayPath: `${pathStart}Group`,
		displayType: 'Label',
		legacyName: 'Exam/Catalogs.Exams.ColumnHeading.Group',
		...sharedProperties,
	},
	SPExamCatalogLabelFilterByGroup: {
		defaultValue: 'Filter by Group',
		description: `Label for: Filter by Group`,
		displayPath: `${pathStart}Filter by Group`,
		displayType: 'Label',
		legacyName: 'Exam/Catalogs.Exams.FieldLabel.FilterByGroup',
		...sharedProperties,
	},
	SPExamCatalogLabelCode: {
		defaultValue: 'Code',
		description: `${columnHeadingDescriptionStart}Code`,
		displayPath: `${pathStart}Code`,
		displayType: 'Label',
		legacyName: 'Exam/Catalogs.Exams.ColumnHeading.ExternalID',
		...sharedProperties,
	},
	SPExamCatalogLabelName: {
		defaultValue: 'Name',
		description: `${columnHeadingDescriptionStart}Name`,
		displayPath: `${pathStart}Name`,
		displayType: 'Label',
		legacyName: 'Exam/Catalogs.Exams.ColumnHeading.Name',
		...sharedProperties,
	},
	SPExamCatalogLabelNameAbbreviation: {
		defaultValue: 'Name Abbr',
		description: `${columnHeadingDescriptionStart}Name Abbr`,
		displayPath: `${pathStart}Name Abbr`,
		displayType: 'Label',
		legacyName: 'Exam/Catalogs.Exams.ColumnHeading.NameAbbreviation',
		...sharedProperties,
	},
	SPExamCatalogLabelStartDate: {
		defaultValue: 'Start Date',
		description: `${columnHeadingDescriptionStart}Start Date`,
		displayPath: `${pathStart}Start Date`,
		displayType: 'Label',
		legacyName: 'Exam/Catalogs.Exams.ColumnHeading.StartDate',
		...sharedProperties,
	},
	SPExamCatalogLabelEndDate: {
		defaultValue: 'End Date',
		description: `${columnHeadingDescriptionStart}End Date`,
		displayPath: `${pathStart}End Date`,
		displayType: 'Label',
		legacyName: 'Exam/Catalogs.Exams.ColumnHeading.EndDate',
		...sharedProperties,
	},
	SPExamCatalogLabelValid: {
		defaultValue: 'Valid (In Months)',
		description: `${columnHeadingDescriptionStart}Valid (In Months)`,
		displayPath: `${pathStart}Valid (In Months)`,
		displayType: 'Label',
		legacyName: 'Exam/Catalogs.Exams.ColumnHeading.Valid',
		...sharedProperties,
	},
	SPExamCatalogLabelScoreReportEnabled: {
		defaultValue: 'Score Report Enabled',
		description: `${columnHeadingDescriptionStart}Score Report Enabled`,
		displayPath: `${pathStart}Score Report Enabled`,
		displayType: 'Label',
		legacyName: 'Exam.ScoreReports/Catalogs.Exams.ColumnHeading.ScoreReport',
		...sharedProperties,
	},
	SPExamCatalogLabelBadgingEnabled: {
		defaultValue: 'Badging Enabled',
		description: `${columnHeadingDescriptionStart}Badging Enabled`,
		displayPath: `${pathStart}Badging Enabled`,
		displayType: 'Label',
		legacyName: 'Share.CertMetricsBadging/Catalogs.Exams.ColumnHeading.BadgingEnabled',
		...sharedProperties,
	},
	SPExamCatalogButtonCriteria: {
		defaultValue: 'Criteria',
		description: `Accessibility text for: Criteria`,
		displayPath: `${pathStart}Criteria`,
		displayType: 'Button',
		legacyName: 'Exam.InformationUrl/Catalogs.Exams.Button.Criteria',
		...sharedProperties,
	},
	SPExamCatalogContentForever: {
		defaultValue: 'Forever',
		description: `Valid period: Forever`,
		displayPath: `${pathStart}Forever`,
		displayType: 'Content',
		legacyName: 'Exam/Catalogs.Exams.Content.Forever',
		...sharedProperties,
	},
};

export type SPExamCatalogLocType = LocMap<SPExamCatalogLocNames>;
